import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Box, 
  Typography, 
  TextField, 
  MenuItem, 
  Paper, 
  Grid, 
  Button, 
  Chip, 
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Avatar
} from "@mui/material";
import { 
  Search, 
  Calendar, 
  ChevronRight, 
  Clock, 
  CheckCircle2, 
  ArrowUpDown,
  Briefcase,
  AlertCircle
} from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const PALETTE = {
  ice: "#C2EDF0",
  sky: "#99D9DF",
  aqua: "#00C0CA",
  teal: "#008B9C",
  azur: "#28708B",
  dark: "#22404D",
  lime: "#8FC440",
  yellow: "#E3E24F",
  pearl: "#EEF3F4"
};

const TareasPorPrograma = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState([]);
  const [nombreFiltro, setNombreFiltro] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("Pendiente");
  const [ordenFecha, setOrdenFecha] = useState("asc");
  const [paginaActual, setPaginaActual] = useState(1);
  const itemsPorPagina = 10;

  useEffect(() => {
    const fetchPedido = async () => {
      try {
        let response = await fetch(`${apiUrl}/Pedido/${id}`, {
          headers: {
            "Content-Type": "application/json",
            apiKey: localStorage.getItem("apiKey"),
          },
        });

        if (response.ok) {
          let data = await response.json();
          setPedidos(data);
        } else {
          manejarErrores(response.status);
        }
      } catch (error) {
        console.error("Error en la solicitud", error);
      }
    };
    fetchPedido();
  }, [id]);

  const manejarErrores = (estatus) => {
    const mensaje = {
      400: "Solicitud incorrecta.",
      401: "No autorizado.",
      403: "Acceso denegado.",
      500: "Error interno del servidor.",
    }[estatus] || "Error inesperado.";

    toast.error(mensaje);
    if (estatus === 401 || estatus === 500) {
      localStorage.clear();
      navigate("/");
    }
    if (estatus === 403) navigate("/dashboardCoordinador");
  };

  // Lógica de filtrado y ordenado
  const pedidosFiltrados = pedidos
    .filter((p) => {
      return (
        (!nombreFiltro || p.nombre.toLowerCase().includes(nombreFiltro.toLowerCase())) &&
        (!estadoFiltro || p.estado.toLowerCase() === estadoFiltro.toLowerCase())
      );
    })
    .sort((a, b) => {
      const fa = new Date(a.fechaLimite);
      const fb = new Date(b.fechaLimite);
      return ordenFecha === "asc" ? fa - fb : fb - fa;
    });

  const totalPages = Math.ceil(pedidosFiltrados.length / itemsPorPagina);
  const pedidosPagidados = pedidosFiltrados.slice(
    (paginaActual - 1) * itemsPorPagina,
    paginaActual * itemsPorPagina
  );

  return (
    <Box sx={{ p: { xs: 2, md: 6 }, pt: { xs: 10, md: 14 }, backgroundColor: "#FFFFFF", minHeight: "100vh" }}>
      
      {/* HEADER SECTION */}
      <Box sx={{ mb: 5, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: PALETTE.dark, width: 56, height: 56 }}>
            <Briefcase color="white" size={28} />
        </Avatar>
        <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, color: PALETTE.dark }}>
                Pedidos por programa
            </Typography>
            
        </Box>
      </Box>

      {/* FILTROS GLASSMORPHISM */}
      <Paper elevation={0} sx={{ 
        p: 2.5, mb: 4, borderRadius: '24px', 
        backgroundColor: PALETTE.pearl, 
        border: `1px solid ${PALETTE.sky}` 
      }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por nombre..."
              value={nombreFiltro}
              onChange={(e) => setNombreFiltro(e.target.value)}
              InputProps={{
                startAdornment: <Search size={18} style={{ marginRight: 8, color: PALETTE.teal }} />,
                sx: { borderRadius: '12px', backgroundColor: 'white' }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              size="small"
              value={estadoFiltro}
              onChange={(e) => setEstadoFiltro(e.target.value)}
              InputProps={{ sx: { borderRadius: '12px', backgroundColor: 'white' } }}
            >
              <MenuItem value="">Todos los estados</MenuItem>
              <MenuItem value="Pendiente">Pendiente</MenuItem>
              <MenuItem value="Terminado">Terminado</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              size="small"
              value={ordenFecha}
              onChange={(e) => setOrdenFecha(e.target.value)}
              InputProps={{ 
                startAdornment: <ArrowUpDown size={18} style={{ marginRight: 8, color: PALETTE.teal }} />,
                sx: { borderRadius: '12px', backgroundColor: 'white' } 
              }}
            >
              <MenuItem value="asc">Más antiguos primero</MenuItem>
              <MenuItem value="desc">Más recientes primero</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* TABLA DE RESULTADOS */}
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '24px', border: '1px solid #eee' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#fbfcfc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>PROGRAMA</TableCell>
              <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>NOMBRE DEL PEDIDO</TableCell>
              <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>FECHA LÍMITE</TableCell>
              <TableCell sx={{ fontWeight: 800, color: PALETTE.azur }}>ESTADO</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pedidosPagidados.map((p) => (
              <TableRow key={p.id} hover sx={{ transition: '0.2s' }}>
                <TableCell sx={{ fontWeight: 700, color: PALETTE.dark }}>
                    {p.programa.nombre}
                </TableCell>
                <TableCell>{p.nombre}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Calendar size={14} color={PALETTE.teal} />
                    {new Date(p.fechaLimite).toLocaleDateString("es-ES")}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={p.estado}
                    size="small"
                    icon={p.estado === "Terminado" ? <CheckCircle2 size={14} /> : <Clock size={14} />}
                    sx={{
                      fontWeight: 800,
                      borderRadius: '8px',
                      bgcolor: p.estado === "Terminado" ? `${PALETTE.lime}15` : `${PALETTE.yellow}25`,
                      color: p.estado === "Terminado" ? PALETTE.lime : "#9A8D00",
                      border: `1px solid ${p.estado === "Terminado" ? PALETTE.lime : PALETTE.yellow}`,
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton 
                    onClick={() => navigate("/pedido/" + p.id)}
                    sx={{ color: PALETTE.dark, '&:hover': { bgcolor: PALETTE.ice, color: PALETTE.teal } }}
                  >
                    <ChevronRight size={22} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {pedidosPagidados.length === 0 && (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <AlertCircle size={40} color="#ccc" style={{ marginBottom: 12 }} />
            <Typography variant="body1" sx={{ color: '#999', fontWeight: 500 }}>
                Este programa no cuenta con pedidos registrados.
            </Typography>
          </Box>
        )}
      </TableContainer>

      {/* PAGINACIÓN ESTILIZADA */}
      {totalPages > 1 && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <Pagination 
                count={totalPages} 
                page={paginaActual} 
                onChange={(e, value) => setPaginaActual(value)}
                sx={{
                    '& .MuiPaginationItem-root': { borderRadius: '12px', fontWeight: 700 },
                    '& .Mui-selected': { bgcolor: `${PALETTE.dark} !important`, color: 'white' }
                }}
            />
        </Box>
      )}
    </Box>
  );
};

export default TareasPorPrograma;